<script setup lang="ts">
import { Button, ZhDetailsFold, ZhPageTitle, ZhStatusPill, ZhTooltip } from '@zerohuman/ui'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { api, CRAFTED_NOT_IN_PLAN_MESSAGE, isCraftedRefusal, type Role, type TaskRow } from '@/api'
import DataTable, { type DataTableColumn } from '@/components/DataTable.vue'
import FirstRunHint from '@/components/FirstRunHint.vue'
import { useListState } from '@/composables/useListState'
import { useUiMode } from '@/composables/useUiMode'
import { isFirstRun } from '@/lib/firstRun'
import { ownerVersionLabel } from '@/lib/ownerVersion'
import { taskLabel } from '@/lib/presentation'

const router = useRouter()
const tasks = ref<TaskRow[]>([])
const roles = ref<Role[]>([])
const total = ref(0)
const page = ref(1)
const limit = ref(25)
const pages = ref(1)
const search = ref('')
const sort = ref('slug')
const order = ref<'ASC' | 'DESC'>('ASC')
const filters = ref<Record<string, string>>({})
/** #633: the search, sort and filters come back on the next visit, in place before the first request. */
const list = useListState('tasks', {
    search: { ref: search },
    sort: { ref: sort },
    order: { ref: order },
    filters: { ref: filters },
})
const loading = ref(false)
const error = ref<string | null>(null)

/**
 * #1749 / #1958: Slug and Kind (llm / deterministic) and their filters are Advanced only; Standard lists the same
 * tasks. The table is always handed every column and hides the Advanced-only ones itself, so the mode never rewrites
 * the columns a person chose to see.
 */
const { mode: uiMode } = useUiMode()
const advancedMode = computed(() => uiMode.value === 'advanced')

/** The filters the list is read with: a Slug or Kind filter set in Advanced does not narrow Standard, which has none. */
function listFilters(): Record<string, string> {
    if (advancedMode.value) {
        return filters.value
    }
    const { kind: _kind, slug: _slug, ...rest } = filters.value
    return rest
}

/**
 * The sidebar switch can flip the mode with this page open. A Slug or Kind filter is read in Advanced and dropped in
 * Standard, so a switch re-reads the list it narrows: Standard is never narrowed by a column it does not show.
 */
watch(advancedMode, () => {
    if (filters.value.kind || filters.value.slug) {
        void load()
    }
})

/** The older Standard code removed Kind from the saved Tasks columns; a list saved before #1958 gets it back once. */
const LEGACY_STRIPPED_COLUMNS = ['kind']

const columns = computed<DataTableColumn[]>(() => [
    { key: 'name', label: 'Name', sort: 'name', type: 'text' },
    { key: 'slug', label: 'Slug', sort: 'slug', type: 'text', advancedOnly: true },
    {
        key: 'kind',
        label: 'Kind',
        sort: 'kind',
        type: 'select',
        advancedOnly: true,
        options: [
            { value: 'llm', label: 'llm' },
            { value: 'deterministic', label: 'deterministic' },
        ],
    },
    {
        key: 'status',
        label: 'Status',
        sort: 'status',
        type: 'select',
        options: [
            { value: 'active', label: 'active' },
            { value: 'deprecated', label: 'deprecated' },
        ],
    },
    {
        // Every run goes to whoever holds this role, on whatever team they sit; a task is never pinned to a member.
        key: 'assigneeRole',
        label: 'Role',
        type: 'select',
        options: roles.value.map(role => ({ value: role.slug, label: role.slug })),
    },
    // The teams that inherit the task: each team where a member holds its role. A task belongs to a role, not a team.
    { key: 'teams', label: 'Teams', type: 'text', filterable: false },
    {
        key: 'pendingRecommendationCount',
        label: 'Recommendations',
        type: 'number',
        filterable: false,
    },
    { key: 'currentVersion.version', label: 'Version', type: 'number', filterable: false, defaultVisible: false },
    {
        key: 'currentVersion.goal',
        label: 'Goal',
        type: 'text',
        filterable: false,
        defaultVisible: false,
        class: 'max-w-sm truncate',
    },
])

async function load(): Promise<void> {
    loading.value = true
    try {
        const listed = await api.tasks({
            page: page.value,
            limit: limit.value,
            search: search.value,
            sort: sort.value,
            order: order.value,
            ...listFilters(),
            // OS tasks are Zero Human's own background work; this page lists only the enterprise's tasks.
            origin: 'tenant',
        })
        tasks.value = listed.data
        total.value = listed.total
        page.value = listed.page
        limit.value = listed.limit
        pages.value = listed.pages
        error.value = null
    } catch {
        error.value = 'Could not load tasks.'
    } finally {
        loading.value = false
    }
}

watch([page, limit, search, sort, order, filters], load, { immediate: true })

/** The enterprise has no task at all, rather than a search or filter narrowing real tasks to zero. */
const noTasks = computed(() => !error.value && isFirstRun(loading.value, total.value, search.value, listFilters()))

void api
    .roles()
    .then(rows => {
        roles.value = rows
    })
    .catch(() => {
        roles.value = []
    })

/** The Name cell: a nameless task reads "Untitled task" in Standard, and falls back to its slug in Advanced only. */
function nameOf(row: Record<string, unknown>): string {
    const task = { name: row.name as string | null | undefined, slug: row.slug as string | null | undefined }
    return taskLabel(task, uiMode.value)
}

function open(row: Record<string, unknown>): void {
    void router.push(`/tasks/${String(row.id)}`)
}

/** A table row as the task the API listed it as. */
function taskOf(row: Record<string, unknown>): TaskRow {
    return row as unknown as TaskRow
}

/** The names of the teams that inherit a task, joined: each team where a member holds its role. */
function teamNames(task: TaskRow): string {
    return (task.teams ?? []).map(team => team.name).join(', ')
}

/**
 * The Version cell's number: a linked clone's is the owner's version it is on (#1260), as its task page shows it, never
 * the clone's own counter. Any other task shows its own version.
 */
function versionOf(row: Record<string, unknown>): number | null {
    const task = taskOf(row)
    return task.ownerVersion?.version ?? task.currentVersion?.version ?? null
}

/**
 * True when the source's newer version adds or drops no tool or gate, needs no input value and leaves the workspace
 * (credentials, write and push rules) as it is, so updating from the list hides nothing the task page's update panel
 * would show. Any other update is reviewed on the task page first.
 */
function updatesQuietly(task: TaskRow): boolean {
    const update = task.sourceUpdate
    return Boolean(
        update &&
            update.addedTools.length === 0 &&
            update.addedGates.length === 0 &&
            !update.removedTools?.length &&
            !update.removedGates?.length &&
            !update.missingInputs?.length &&
            !update.workspaceChanged,
    )
}

/** The row whose update from its source is in flight, so its button cannot be pressed twice. */
const updating = ref<string | null>(null)

/**
 * Moves a linked clone onto its source's newer version, from the list (#414), as the task page's Update button does. An
 * update that changes the task's tools, gates, inputs or workspace opens the task page instead, where the panel lists
 * them.
 */
async function updateFromSource(task: TaskRow): Promise<void> {
    if (!task.sourceUpdate || updating.value) {
        return
    }
    if (!updatesQuietly(task)) {
        void router.push(`/tasks/${task.id}`)
        return
    }
    updating.value = task.id
    error.value = null
    try {
        await api.updateTaskFromSource(task.id, task.currentVersionId ?? null)
        await load()
    } catch (caught) {
        // A Zero Human source the plan does not include (#449): the same sentence the task page shows.
        if (isCraftedRefusal(caught)) error.value = CRAFTED_NOT_IN_PLAN_MESSAGE
        else error.value = caught instanceof Error ? `Could not update (${caught.message}).` : 'Could not update.'
    } finally {
        updating.value = null
    }
}
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<div class="flex items-center justify-between gap-4">
			<ZhPageTitle>Tasks</ZhPageTitle>
			<Button as="a" href="/tasks/new" data-testid="create-button" @click.prevent="router.push('/tasks/new')">
				Create
			</Button>
		</div>
		<!-- #1762: one plain sentence opens the page; how the list works sits behind "How this works", closed until asked
		for. Standard and Advanced read the same: it is plain copy, nothing to hide. -->
		<div class="space-y-2">
			<p class="text-muted-foreground text-sm">The jobs your teammates know how to do.</p>
			<ZhDetailsFold label="How this works">
				<p class="text-sm">
					Click a row to open its overview, pending recommendations and runs. Editing a task saves a new version.
				</p>
			</ZhDetailsFold>
		</div>
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<!-- No task at all: say what will appear and offer the one action that adds one. A search or filter that
		narrows real tasks to zero keeps the table and its plain "No rows.", or there would be no way to clear it. -->
		<FirstRunHint
			v-if="noTasks"
			lead="No tasks yet. Your tasks appear here: what each one does, who it runs as, and how its runs went."
			:action="{ label: 'Create a task', to: '/tasks/new' }"
		/>
		<DataTable
			v-else
			storage-key="tasks"
			:restore-legacy-keys="LEGACY_STRIPPED_COLUMNS"
			:columns="columns"
			:rows="tasks as unknown as Record<string, unknown>[]"
			:total="total"
			:page="page"
			:limit="limit"
			:pages="pages"
			:loading="loading"
			:search="search"
			:sort="sort"
			:order="order"
			:filters="filters"
			:list-state="list"
			@update:page="page = $event"
			@update:limit="limit = $event"
			@update:search="search = $event"
			@update:sort="sort = $event"
			@update:order="order = $event"
			@update:filters="filters = $event"
			@row-click="open"
		>
			<template #cell-name="{ row }">
				<span class="inline-flex items-center gap-2">
					<span>{{ nameOf(row) }}</span>
					<ZhTooltip
						v-if="Number(row.pendingRecommendationCount) > 0"
						:text="`${row.pendingRecommendationCount} pending recommendation(s)`"
					>
						<ZhStatusPill tone="system" :count="Number(row.pendingRecommendationCount)" />
					</ZhTooltip>
				</span>
			</template>
			<template #cell-assigneeRole="{ row, value }">
				<span v-if="row.origin === 'os'">OS</span>
				<span v-else-if="value">{{ row.assigneeRoleName || value }}</span>
				<span v-else class="text-muted-foreground">No role</span>
			</template>
			<template #cell-teams="{ row }">
				<span v-if="taskOf(row).teams?.length" data-testid="task-teams">{{ teamNames(taskOf(row)) }}</span>
				<span v-else class="text-muted-foreground">—</span>
			</template>
			<!-- A linked clone shows the owner's version it is on, and the newer one it can update to (#1260). -->
			<template #cell-currentVersion.version="{ row }">
				<span class="inline-flex items-center gap-2">
					<ZhTooltip v-if="taskOf(row).ownerVersion" :text="ownerVersionLabel(taskOf(row).ownerVersion?.ownerName, nameOf(row), versionOf(row) ?? 0)">
						<span data-testid="task-version">{{ versionOf(row) }}</span>
					</ZhTooltip>
					<span v-else data-testid="task-version">{{ versionOf(row) ?? '—' }}</span>
					<Button
						v-if="taskOf(row).sourceUpdate"
						type="button"
						size="sm"
						variant="outline"
						data-testid="task-source-update"
						:disabled="updating !== null"
						@click.stop="updateFromSource(taskOf(row))"
					>
						{{ updatesQuietly(taskOf(row)) ? 'Update to' : 'Review' }} v{{ taskOf(row).sourceUpdate?.sourceVersion }}
					</Button>
				</span>
			</template>
			<template #cell-pendingRecommendationCount="{ value }">
				<ZhStatusPill v-if="Number(value) > 0" tone="system" :count="Number(value)" />
				<span v-else class="text-muted-foreground">—</span>
			</template>
		</DataTable>
	</section>
</template>
